/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { classes } from '@adobe-commerce/elsie/lib';
import { Locker, EyeClose, Eye } from '@adobe-commerce/elsie/icons';
import { Button, Field, Input, Icon } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  PasswordStatusIndicator,
  PasswordStatusIndicatorProps,
} from './PasswordStatusIndicator';
import { HTMLAttributes } from 'preact/compat';

import '@adobe-commerce/elsie/components/InputPassword/InputPassword.css';

export interface InputPasswordProps
  extends PasswordStatusIndicatorProps,
    Omit<HTMLAttributes<HTMLDivElement>, 'minLength'> {
  placeholder?: string;
  floatingLabel?: string;
  defaultValue?: string;
  className?: string;
  name?: string;
  autoComplete?: string;
  errorMessage?: string | undefined;
  required?: boolean;
  hideStatusIndicator?: boolean;
  onValue?: (value: any) => void;
  onBlur?: (event: Event) => void;
}

export const InputPassword: FunctionComponent<InputPasswordProps> = ({
  placeholder,
  floatingLabel,
  children,
  name,
  required,
  className,
  minLength,
  autoComplete,
  defaultValue = '',
  hideStatusIndicator = false,
  uniqueSymbolsStatus,
  validateLengthConfig,
  requiredCharacterClasses,
  errorMessage,
  onValue,
  onBlur,
  ...props
}) => {
  const translations = useText({
    placeholder: 'Dropin.InputPassword.placeholder',
    floatingLabel: 'Dropin.InputPassword.floatingLabel',
    buttonShowTitle: 'Dropin.InputPassword.buttonShowTitle',
    buttonHideTitle: 'Dropin.InputPassword.buttonHideTitle',
  });

  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  }, []);

  const title = !showPassword
    ? translations.buttonShowTitle
    : translations.buttonHideTitle;

  return (
    <div
      data-testid="passwordFieldInput"
      className={classes([
        'dropin-input-password',
        ['dropin-input-password--error', errorMessage],
        className,
      ])}
      {...props}
    >
      <Field error={errorMessage}>
        <Input
          autoComplete={autoComplete}
          name={name ?? 'password'}
          type={!showPassword ? 'password' : 'text'}
          placeholder={placeholder || translations.placeholder}
          floatingLabel={floatingLabel || translations.floatingLabel}
          aria-label={translations.placeholder}
          aria-required={required || true}
          aria-invalid={!!errorMessage}
          aria-describedby="password-feedback"
          required={required || false}
          value={defaultValue}
          onValue={onValue}
          icon={<Locker />}
          onBlur={onBlur}
          data-testid="passwordInput"
        />
      </Field>
      <Button
        aria-label={title}
        title={title}
        type={'button'}
        data-testid="toggle-password-icon"
        variant="tertiary"
        className={classes([
          'dropin-input-password__eye-icon',
          `dropin-input-password__eye-icon--${showPassword ? 'show' : 'hide'}`,
          className,
        ])}
        onClick={toggleShowPassword}
      >
        <Icon
          focusable={false}
          aria-hidden={showPassword}
          source={!showPassword ? EyeClose : Eye}
        />
      </Button>
      {hideStatusIndicator ? null : (
        <PasswordStatusIndicator
          minLength={minLength}
          requiredCharacterClasses={requiredCharacterClasses}
          validateLengthConfig={validateLengthConfig}
          uniqueSymbolsStatus={uniqueSymbolsStatus}
        />
      )}
      {children}
    </div>
  );
};
